// @ts-nocheck
/**
 * Krebs Valve Configurator admin page (storefront-hosted).
 *
 */
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import FieldDefinition, {
    ExcludeFromNavigation,
    HideFooter,
    HideFromSearchEngines,
    HideFromSiteSearch,
    HideHeader,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import * as React from "react";
import { useSelector } from "react-redux";

// -----------------------------------------------------------------------
// Entity descriptors. Identical shape to the legacy ENTITIES object in
// valve-config.html so the backend payloads remain unchanged.
// -----------------------------------------------------------------------
type FieldDef = {
    key: string;
    label: string;
    type: "text" | "number" | "bool";
    max?: number;
    required?: boolean;
    nullable?: boolean;
    default?: unknown;
    gridFormatter?: "image";
};

type EntityDef = {
    label: string;
    singular?: string;
    api: string;
    noIsActive?: boolean;
    fields: FieldDef[];
    detail?: EntityDef & {
        parentField: string;
        childParentField: string;
        parentNotice?: (parentRow: Record<string, unknown>) => string;
        parentLabel?: (parentRow: Record<string, unknown>) => string;
    };
};

const ENTITIES: Record<string, EntityDef> = {
    valvetypes: {
        label: "Valve Types",
        api: "/api/v1/krebs/valvetypes",
        fields: [
            { key: "code", label: "Code", type: "text", max: 10, required: true },
            { key: "altCode", label: "Alt Code", type: "text", max: 10 },
            { key: "label", label: "Label", type: "text", max: 150, required: true },
            { key: "subtitle", label: "Subtitle", type: "text", max: 200 },
            { key: "previewImageUrl", label: "Preview Image URL", type: "text", max: 500, gridFormatter: "image" },
            { key: "emitsAutolubricationNote", label: "Emits Autolube Note", type: "bool" },
            { key: "sortOrder", label: "Sort Order", type: "number", default: 0 },
            { key: "isActive", label: "Active", type: "bool", default: true },
        ],
        detail: {
            label: "Sizes",
            singular: "Size",
            api: "/api/v1/krebs/valvesizes",
            parentField: "code",
            childParentField: "series",
            fields: [
                { key: "sizeInch", label: "Size (in)", type: "number", required: true },
                { key: "label", label: "Label", type: "text", max: 20, required: true },
                { key: "sortOrder", label: "Sort Order", type: "number", default: 0 },
                { key: "isActive", label: "Active", type: "bool", default: true },
            ],
        },
    },
    gatematerials: {
        label: "Gate Materials",
        api: "/api/v1/krebs/gatematerials",
        fields: [
            { key: "code", label: "Code", type: "text", max: 5, required: true },
            { key: "label", label: "Label", type: "text", max: 150, required: true },
            { key: "pnSuffix", label: "PN Suffix", type: "text", max: 30 },
            { key: "quoteToOrder", label: "Quote To Order", type: "bool" },
            { key: "previewImageUrl", label: "Preview Image URL", type: "text", max: 500, gridFormatter: "image" },
            { key: "sortOrder", label: "Sort Order", type: "number", default: 0 },
            { key: "isActive", label: "Active", type: "bool", default: true },
        ],
    },
    elastomers: {
        label: "Elastomers",
        api: "/api/v1/krebs/elastomers",
        fields: [
            { key: "code", label: "Code", type: "text", max: 5, required: true },
            { key: "label", label: "Label", type: "text", max: 150, required: true },
            { key: "pnSuffix", label: "PN Suffix", type: "text", max: 30 },
            { key: "quoteToOrder", label: "Quote To Order", type: "bool" },
            { key: "previewImageUrl", label: "Preview Image URL", type: "text", max: 500, gridFormatter: "image" },
            { key: "sortOrder", label: "Sort Order", type: "number", default: 0 },
            { key: "isActive", label: "Active", type: "bool", default: true },
        ],
    },
    acmosubtypes: {
        label: "ACMO Sub-Types",
        api: "/api/v1/krebs/acmosubtypes",
        fields: [
            { key: "actuatorCode", label: "Actuator Code", type: "text", max: 5, required: true },
            { key: "pnBase", label: "PN Base", type: "text", max: 30, required: true },
            { key: "description", label: "Description", type: "text", max: 200, required: true },
            { key: "minSize", label: "Min Size", type: "number", nullable: true },
            { key: "maxSize", label: "Max Size", type: "number", nullable: true },
            { key: "sortOrder", label: "Sort Order", type: "number", default: 0 },
            { key: "isActive", label: "Active", type: "bool", default: true },
        ],
    },
    paintoptions: {
        label: "Paint Options",
        api: "/api/v1/krebs/paintoptions",
        fields: [
            { key: "label", label: "Label", type: "text", max: 200, required: true },
            { key: "pnImpact", label: "PN Impact", type: "bool" },
            { key: "isDefault", label: "Default", type: "bool" },
            { key: "sortOrder", label: "Sort Order", type: "number", default: 0 },
            { key: "isActive", label: "Active", type: "bool", default: true },
        ],
    },
    hardwarelookups: {
        label: "Hardware Lookups",
        api: "/api/v1/krebs/hardwarelookups",
        noIsActive: true,
        fields: [
            { key: "flangeCode", label: "Flange Code", type: "text", max: 5, required: true },
            { key: "sizeInch", label: "Size (in)", type: "number", required: true, default: 0 },
            { key: "partNumber", label: "Part Number", type: "text", max: 50, required: true },
            { key: "qty", label: "Qty", type: "number", default: 1 },
            { key: "isConfirmed", label: "Confirmed", type: "bool", default: true },
        ],
    },
    flangedrillingrules: {
        label: "Flange Drilling Rules",
        singular: "Flange Drilling Rule",
        api: "/api/v1/krebs/flangedrillingrules",
        fields: [
            { key: "code", label: "Code", type: "text", max: 10, required: true },
            { key: "label", label: "Label", type: "text", max: 200, required: true },
            { key: "minSize", label: "Min Size (in)", type: "number", required: true, default: 0 },
            { key: "maxSize", label: "Max Size (in)", type: "number", required: true, default: 0 },
            { key: "sortOrder", label: "Sort Order", type: "number", default: 0 },
            { key: "isActive", label: "Active", type: "bool", default: true },
        ],
    },
    gatecoatings: {
        label: "Gate Coatings",
        singular: "Gate Coating",
        api: "/api/v1/krebs/gatecoatings",
        fields: [
            { key: "label", label: "Label", type: "text", max: 200, required: true },
            { key: "note", label: "Note", type: "text", max: 500 },
            { key: "isDefault", label: "Default", type: "bool" },
            { key: "sortOrder", label: "Sort Order", type: "number", default: 0 },
            { key: "isActive", label: "Active", type: "bool", default: true },
        ],
    },
    valvematerials: {
        label: "Valve Materials",
        singular: "Valve Material",
        api: "/api/v1/krebs/valvematerials",
        fields: [
            { key: "label", label: "Label", type: "text", max: 200, required: true },
            { key: "isDefault", label: "Default", type: "bool" },
            { key: "sortOrder", label: "Sort Order", type: "number", default: 0 },
            { key: "isActive", label: "Active", type: "bool", default: true },
        ],
    },
    hardwarematerials: {
        label: "Hardware Materials",
        singular: "Hardware Material",
        api: "/api/v1/krebs/hardwarematerials",
        fields: [
            { key: "label", label: "Label", type: "text", max: 200, required: true },
            { key: "isDefault", label: "Default", type: "bool" },
            { key: "sortOrder", label: "Sort Order", type: "number", default: 0 },
            { key: "isActive", label: "Active", type: "bool", default: true },
        ],
    },
    bomlines: {
        label: "BOM / Spare Parts Lines",
        singular: "BOM Line",
        api: "/api/v1/krebs/bomlines",
        fields: [
            { key: "series", label: "Series", type: "text", max: 10, required: true },
            { key: "lineKey", label: "Line Key", type: "text", max: 40, required: true },
            { key: "itemCode", label: "Item", type: "text", max: 10, required: true },
            { key: "pnFormat", label: "PN Format", type: "text", max: 200 },
            { key: "descriptionKey", label: "Description Key", type: "text", max: 150, required: true },
            { key: "defaultPreviewImageUrl", label: "Default Preview Image URL", type: "text", max: 500, gridFormatter: "image" },
            { key: "qty", label: "Qty", type: "number", default: 1 },
            { key: "isOptional", label: "Optional (Spare Parts only)", type: "bool" },
            { key: "sortOrder", label: "Sort Order", type: "number", default: 0 },
            { key: "isActive", label: "Active", type: "bool", default: true },
        ],
    },
    valveactuators: {
        label: "Actuators",
        singular: "Actuator",
        api: "/api/v1/krebs/valveactuators",
        fields: [
            { key: "label", label: "Label", type: "text", max: 300, required: true },
            { key: "code", label: "Code", type: "text", max: 10 },
            { key: "pnBase", label: "PN Base", type: "text", max: 60 },
            { key: "quoteToOrder", label: "Quote To Order", type: "bool" },
            { key: "useAcmoLookup", label: "Use ACMO Lookup", type: "bool" },
            { key: "noActuator", label: "No Actuator", type: "bool" },
            { key: "isAvailableForAllSizes", label: "All Sizes", type: "bool" },
            { key: "qtoMessage", label: "QTO Message", type: "text", max: 1000 },
            { key: "previewImageUrl", label: "Preview Image URL", type: "text", max: 500, gridFormatter: "image" },
            { key: "sortOrder", label: "Sort Order", type: "number", default: 0 },
            { key: "isActive", label: "Active", type: "bool", default: true },
        ],
        detail: {
            label: "Availability",
            singular: "Availability Row",
            api: "/api/v1/krebs/valveactuatoravailability",
            parentField: "id",
            childParentField: "actuatorId",
            parentNotice: parentRow =>
                parentRow.isAvailableForAllSizes
                    ? '<div class="empty" style="padding:8px;">This actuator is marked <strong>Available for All Sizes</strong>; per-size rows are not required.</div>'
                    : "",
            fields: [
                { key: "series", label: "Series", type: "text", max: 10, required: true },
                { key: "sizeInch", label: "Size (in)", type: "number", required: true },
            ],
        },
    },
};

const ENTITY_ORDER = [
    "valvetypes",
    "valveactuators",
    "gatematerials",
    "elastomers",
    "acmosubtypes",
    "paintoptions",
    "hardwarelookups",
    "flangedrillingrules",
    "gatecoatings",
    "valvematerials",
    "hardwarematerials",
    "bomlines",
];

// CSS preserved from valve-config.html with the layout chrome (header /
// sidenav) removed - the storefront page already provides those.
const STYLES = `
.vca-root { --primary: #003a70; --border: #d6d6d6; --bg: #f6f7f9; --danger: #b00020; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #222; padding: 16px; }
.vca-root * { box-sizing: border-box; }
.vca-root .layout { display: flex; align-items: stretch; min-height: 600px; background: var(--bg); border: 1px solid var(--border); }
.vca-root .sidenav { background: #002650; width: 220px; flex: 0 0 220px; padding: 12px 0; overflow-y: auto; }
.vca-root .sidenav nav { display: flex; flex-direction: column; }
.vca-root .sidenav nav a { color: #fff; text-decoration: none; padding: 10px 18px; font-size: 13px; opacity: 0.8; border-left: 3px solid transparent; cursor: pointer; }
.vca-root .sidenav nav a:hover { opacity: 1; background: rgba(255,255,255,0.08); }
.vca-root .sidenav nav a.active { background: rgba(255,255,255,0.18); opacity: 1; font-weight: 600; border-left-color: #fff; }
.vca-root main { padding: 24px; flex: 1 1 auto; min-width: 0; }
@media (max-width: 720px) {
    .vca-root .layout { flex-direction: column; }
    .vca-root .sidenav { width: 100%; flex: 0 0 auto; padding: 4px 8px; overflow-x: auto; }
    .vca-root .sidenav nav { flex-direction: row; flex-wrap: wrap; gap: 4px; }
    .vca-root .sidenav nav a { border-left: none; border-bottom: 3px solid transparent; padding: 8px 12px; }
    .vca-root .sidenav nav a.active { border-left-color: transparent; border-bottom-color: #fff; }
}
.vca-root .toolbar { display: flex; gap: 8px; margin-bottom: 16px; align-items: center; }
.vca-root .toolbar label { font-size: 13px; color: #555; }
.vca-root button { font: inherit; cursor: pointer; padding: 8px 14px; border: 1px solid var(--primary); background: #fff; color: var(--primary); border-radius: 3px; }
.vca-root button.primary { background: var(--primary); color: #fff; }
.vca-root button.danger { border-color: var(--danger); color: var(--danger); }
.vca-root button.danger.primary { background: var(--danger); color: #fff; }
.vca-root button:disabled { opacity: 0.5; cursor: not-allowed; }
.vca-root table { width: 100%; border-collapse: collapse; background: #fff; box-shadow: 0 1px 2px rgba(0,0,0,0.06); }
.vca-root th, .vca-root td { padding: 10px 12px; text-align: left; border-bottom: 1px solid var(--border); font-size: 14px; vertical-align: middle; }
.vca-root th { background: #fafbfc; font-weight: 600; color: #333; }
.vca-root tr.inactive td { color: #999; }
.vca-root .preview { width: 60px; height: 40px; object-fit: contain; }
.vca-root .actions { white-space: nowrap; display: flex; gap: 6px; }
.vca-root .modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: none; align-items: center; justify-content: center; z-index: 100; }
.vca-root .modal-bg.show { display: flex; }
.vca-root .modal { background: #fff; padding: 24px; border-radius: 4px; width: 560px; max-width: 95vw; max-height: 90vh; overflow: auto; }
.vca-root .modal h2 { margin: 0 0 16px; font-size: 18px; }
.vca-root .field { margin-bottom: 14px; }
.vca-root .field label { display: block; font-size: 13px; color: #444; margin-bottom: 4px; }
.vca-root .field input[type=text], .vca-root .field input[type=number] { width: 100%; padding: 8px; border: 1px solid var(--border); border-radius: 3px; font: inherit; }
.vca-root .field.inline { display: flex; align-items: center; gap: 8px; }
.vca-root .field.inline label { margin: 0; }
.vca-root .modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 18px; }
.vca-root .toast { position: fixed; top: 16px; right: 16px; background: #323232; color: #fff; padding: 10px 16px; border-radius: 3px; opacity: 0; transition: opacity 0.2s; z-index: 200; max-width: 60vw; }
.vca-root .toast.show { opacity: 1; }
.vca-root .toast.error { background: var(--danger); }
.vca-root .empty, .vca-root .error-msg { padding: 24px; text-align: center; color: #777; }
.vca-root .error-msg { color: var(--danger); }
.vca-root .req { color: var(--danger); }
`;

// Static skeleton mirroring the original valve-config.html layout. Filled in
// by the imperative wiring below.
const SKELETON = `
<div class="layout">
    <aside class="sidenav"><nav data-vca="entityNav"></nav></aside>
    <main>
        <div class="toolbar">
            <button class="primary" data-vca="btnNew">+ New</button>
            <button data-vca="btnReload">Reload</button>
            <label data-vca="chkInactiveLabel" style="margin-left:auto;"><input type="checkbox" data-vca="chkInactive" checked /> Show inactive</label>
        </div>
        <div data-vca="tableHost">Loading...</div>
    </main>
</div>
<div class="modal-bg" data-vca="editorBg">
    <div class="modal">
        <h2 data-vca="editorTitle">Edit</h2>
        <form data-vca="editorForm">
            <input type="hidden" data-vca="fId" />
            <div data-vca="editorFields"></div>
            <div class="modal-actions">
                <button type="button" data-vca="btnCancel">Cancel</button>
                <button type="submit" class="primary">Save</button>
            </div>
        </form>
    </div>
</div>
<div class="toast" data-vca="toast"></div>
`;

function escapeHtml(s: unknown): string {
    if (s === null || s === undefined) {
        return "";
    }
    return String(s).replace(
        /[&<>"']/g,
        c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string),
    );
}

// All wiring lives here so the React layer stays a thin shell. Returns a
// teardown function used by the effect cleanup.
//
// `getAccessToken` returns the current storefront OAuth bearer token from
// the Redux store. Insite's `/api/v1/...` endpoints authenticate via the
// `Authorization: Bearer <token>` header rather than session cookies, so
// every request below must inject the latest token at call time.
function wireAdminUi(host: HTMLElement, getAccessToken: () => string | undefined): () => void {
    const $ = (key: string) => host.querySelector<HTMLElement>(`[data-vca="${key}"]`)!;

    // Resolve entity from ?type=... falling back to the first entry.
    const qs = new URLSearchParams(window.location.search);
    const requested = (qs.get("type") || "valvetypes").toLowerCase();
    const entityKey = ENTITIES[requested] ? requested : "valvetypes";
    const entity = ENTITIES[entityKey];

    const navEl = $("entityNav");
    const tableHost = $("tableHost") as HTMLDivElement;
    const editorBg = $("editorBg");
    const editorTitle = $("editorTitle");
    const editorFields = $("editorFields");
    const form = $("editorForm") as HTMLFormElement;
    const toast = $("toast");
    const fIdInput = $("fId") as HTMLInputElement;
    const chkInactive = $("chkInactive") as HTMLInputElement;
    const chkInactiveLabel = $("chkInactiveLabel");
    const btnNew = $("btnNew");
    const btnReload = $("btnReload");
    const btnCancel = $("btnCancel");

    // Resolve DOM nodes lazily on every read. React 18 StrictMode can
    // double-invoke our effect, and `dangerouslySetInnerHTML` rewrites the
    // host's children on each render, which detaches whatever DOM node we
    // captured at wiring time. Querying the live DOM each time avoids the
    // "data loaded but Loading… still visible" / "buttons do nothing"
    // symptoms that come from operating on detached elements.
    const live = <T extends HTMLElement = HTMLElement>(key: string, fallback: HTMLElement): T =>
        (host.querySelector<T>(`[data-vca="${key}"]`) ?? (fallback as T)) as T;
    const getNavEl = () => live("entityNav", navEl);
    const getTableHost = () => live<HTMLDivElement>("tableHost", tableHost);
    const getEditorBg = () => live("editorBg", editorBg);
    const getEditorTitle = () => live("editorTitle", editorTitle);
    const getEditorFields = () => live("editorFields", editorFields);
    const getForm = () => live<HTMLFormElement>("editorForm", form);
    const getToast = () => live("toast", toast);
    const getFIdInput = () => live<HTMLInputElement>("fId", fIdInput);
    const getChkInactive = () => live<HTMLInputElement>("chkInactive", chkInactive);
    const getChkInactiveLabel = () => live("chkInactiveLabel", chkInactiveLabel);

    // Sidebar nav. Build via innerHTML so the assignment is atomic and
    // idempotent — re-running the effect (StrictMode) or a parent re-render
    // that re-applies dangerouslySetInnerHTML cannot leave us with
    // half-populated state.
    const buildSidebar = () => {
        const nav = getNavEl();
        const basePath = window.location.pathname;
        nav.innerHTML = ENTITY_ORDER
            .map(k => {
                const cls = k === entityKey ? ' class="active"' : "";
                // Anchor at the current page path so Spire's SPA router
                // doesn't resolve a bare query string against the site root
                // and bounce us to the home page.
                return `<a href="${escapeHtml(basePath)}?type=${k}" data-vca-nav="${k}"${cls}>${escapeHtml(ENTITIES[k].label)}</a>`;
            })
            .join("");
        // eslint-disable-next-line no-console
        console.debug("[ValveConfigAdmin] sidebar built, count=", nav.children.length);
    };
    buildSidebar();

    // Repopulate the sidebar if React/parent re-render wipes our innerHTML.
    const sidebarObserver = new MutationObserver(() => {
        const nav = getNavEl();
        if (nav && nav.children.length === 0) {
            buildSidebar();
        }
    });
    sidebarObserver.observe(host, { childList: true, subtree: true });

    // Bearer-authed API caller. The storefront authenticates `/api/v1/...`
    // requests with an OAuth access token (see SessionService.getHeader);
    // session cookies alone do not establish HttpContext.Current.User for
    // these routes.
    const api = (
        desc: EntityDef,
        method: string,
        path: string,
        body?: unknown,
    ): Promise<any> => {
        const headers: Record<string, string> = {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
        };
        const token = getAccessToken();
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        const opts: RequestInit = {
            method,
            credentials: "same-origin",
            cache: "no-store",
            headers,
        };
        if (body !== undefined) {
            headers["Content-Type"] = "application/json";
            opts.body = JSON.stringify(body);
        }
        return fetch(desc.api + (path || ""), opts).then(r => {
            if (r.status === 401 || r.status === 403) {
                throw new Error(
                    "Not authorized. You must be signed in to the storefront with the KrebsValveConfigAdmin role.",
                );
            }
            const contentType = r.headers.get("Content-Type") || "";
            return r.text().then(text => {
                let data: any = null;
                const looksLikeJson = contentType.includes("json") || /^[\s]*[\[{]/.test(text || "");
                if (looksLikeJson) {
                    try {
                        data = text ? JSON.parse(text) : null;
                    } catch (e) {
                        throw new Error(
                            `Expected JSON from ${desc.api}${path || ""} but got invalid JSON (status ${r.status}, content-type "${contentType}"): ${text.slice(0, 200)}`,
                        );
                    }
                } else if (r.ok && !text) {
                    data = null;
                } else {
                    throw new Error(
                        `Expected JSON from ${desc.api}${path || ""} but received "${contentType}" (status ${r.status}). First 200 chars: ${text.slice(0, 200)}`,
                    );
                }
                if (!r.ok) {
                    const msg =
                        data && typeof data === "object" && data.Message
                            ? data.Message
                            : typeof data === "string"
                            ? data
                            : `HTTP ${r.status}`;
                    throw new Error(msg);
                }
                return data;
            });
        });
    };

    function showToast(msg: string, isError?: boolean): void {
        const t = getToast();
        t.textContent = msg;
        t.classList.toggle("error", !!isError);
        t.classList.add("show");
        window.setTimeout(() => t.classList.remove("show"), 3500);
    }

    function buildEditor(desc: EntityDef, lockedField: { key: string; value: unknown } | null): void {
        const editorFieldsEl = getEditorFields();
        editorFieldsEl.innerHTML = "";
        desc.fields.forEach(f => {
            const div = document.createElement("div");
            const inputId = `f_${f.key}`;
            const locked = !!(lockedField && lockedField.key === f.key);
            if (f.type === "bool") {
                div.className = "field inline";
                div.innerHTML =
                    `<input type="checkbox" id="${inputId}"${locked ? " disabled" : ""} />` +
                    `<label for="${inputId}">${escapeHtml(f.label)}</label>`;
            } else {
                div.className = "field";
                const req = f.required ? ' <span class="req">*</span>' : "";
                const maxStr = f.max ? ` (max ${f.max})` : "";
                const inputType = f.type === "number" ? "number" : "text";
                let attrs = "";
                if (f.max) {
                    attrs += ` maxlength="${f.max}"`;
                }
                if (f.required) {
                    attrs += " required";
                }
                if (locked) {
                    attrs += " readonly";
                }
                div.innerHTML =
                    `<label for="${inputId}">${escapeHtml(f.label)}${req}${maxStr}</label>` +
                    `<input type="${inputType}" id="${inputId}"${attrs} />`;
            }
            editorFieldsEl.appendChild(div);
        });
        if (lockedField && !desc.fields.some(f => f.key === lockedField.key)) {
            const hidden = document.createElement("input");
            hidden.type = "hidden";
            hidden.id = `f_${lockedField.key}`;
            editorFieldsEl.appendChild(hidden);
        }
    }

    function renderCell(field: FieldDef, val: unknown): string {
        if (field.type === "bool") {
            return val ? "Yes" : "No";
        }
        if (field.gridFormatter === "image") {
            return val ? `<img class="preview" src="${escapeHtml(val)}" alt="" />` : "";
        }
        if (val === null || val === undefined) {
            return "";
        }
        return escapeHtml(val);
    }

    function buildRowsHtml(desc: EntityDef, rows: any[], hasDetail: boolean): string {
        let html = "";
        rows.forEach(r => {
            const cls = desc.noIsActive ? "" : r.isActive === false ? "inactive" : "";
            html += `<tr class="${cls}" data-id="${r.id}">`;
            if (hasDetail) {
                html += `<td><button data-action="toggle" data-id="${r.id}" title="Show sizes">&#9654;</button></td>`;
            }
            desc.fields.forEach(f => {
                html += `<td>${renderCell(f, r[f.key])}</td>`;
            });
            html +=
                `<td class="actions">` +
                `<button data-action="edit" data-id="${r.id}">Edit</button>` +
                `<button class="danger" data-action="del" data-id="${r.id}">Delete</button>` +
                `</td></tr>`;
            if (hasDetail) {
                const colspan = desc.fields.length + 2;
                html +=
                    `<tr class="detail-row" data-detail-for="${r.id}" style="display:none;">` +
                    `<td colspan="${colspan}" style="background:#fafbfc;padding:12px 24px;">` +
                    `<div data-detail-host="${r.id}">Loading...</div>` +
                    `</td></tr>`;
            }
        });
        return html;
    }

    let parentRowsById: Record<string, any> = {};

    function renderParent(rows: any[]): void {
        const tHost = getTableHost();
        if (!rows || rows.length === 0) {
            tHost.innerHTML = '<div class="empty">No rows yet. Click "+ New" to add one.</div>';
            return;
        }
        const hasDetail = !!entity.detail;
        let html = "<table><thead><tr>";
        if (hasDetail) {
            html += '<th style="width:32px;"></th>';
        }
        entity.fields.forEach(f => {
            html += `<th>${escapeHtml(f.label)}</th>`;
        });
        html += "<th>Actions</th></tr></thead><tbody>";
        html += buildRowsHtml(entity, rows, hasDetail);
        html += "</tbody></table>";
        tHost.innerHTML = html;
        parentRowsById = {};
        rows.forEach(r => {
            parentRowsById[r.id] = r;
        });
        wireGridButtons(tHost, entity, null);
    }

    function renderChild(hostEl: HTMLElement, desc: EntityDef, rows: any[], parentRow: any): void {
        const detail = entity.detail!;
        const detailDesc = desc as EntityDef & {
            parentNotice?: (parentRow: Record<string, unknown>) => string;
            parentLabel?: (parentRow: Record<string, unknown>) => string;
        };
        const noticeText =
            typeof detailDesc.parentNotice === "function" ? detailDesc.parentNotice(parentRow) || "" : "";
        const headerLabel =
            typeof detailDesc.parentLabel === "function"
                ? detailDesc.parentLabel(parentRow)
                : parentRow.label || parentRow.code || parentRow[detail.parentField];
        let html =
            '<div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">' +
            `<strong>${escapeHtml(desc.label)} for ${escapeHtml(headerLabel)}</strong>` +
            `<button data-action="child-new" class="primary" style="margin-left:auto;">+ New ${escapeHtml(
                desc.singular || desc.label,
            )}</button>` +
            "</div>";
        if (noticeText) {
            html += noticeText;
        }
        if (!rows || rows.length === 0) {
            html += `<div class="empty" style="padding:12px;">No ${escapeHtml(desc.label.toLowerCase())} yet.</div>`;
            hostEl.innerHTML = html;
        } else {
            html += "<table><thead><tr>";
            desc.fields.forEach(f => {
                html += `<th>${escapeHtml(f.label)}</th>`;
            });
            html += "<th>Actions</th></tr></thead><tbody>";
            html += buildRowsHtml(desc, rows, false);
            html += "</tbody></table>";
            hostEl.innerHTML = html;
        }
        wireGridButtons(hostEl, desc, parentRow);
    }

    function wireGridButtons(scope: HTMLElement, desc: EntityDef, parentRow: any): void {
        scope.querySelectorAll<HTMLButtonElement>("button[data-action]").forEach(btn => {
            const wired = (btn as HTMLButtonElement & { _wired?: boolean })._wired;
            if (wired) {
                return;
            }
            (btn as HTMLButtonElement & { _wired?: boolean })._wired = true;
            btn.addEventListener("click", e => {
                e.stopPropagation();
                const id = btn.getAttribute("data-id") ?? "";
                const action = btn.getAttribute("data-action");
                if (action === "toggle") {
                    toggleDetail(id, btn);
                } else if (action === "edit") {
                    openEditor(desc, id, parentRow);
                } else if (action === "del") {
                    confirmDelete(desc, id, parentRow);
                } else if (action === "child-new") {
                    openEditor(desc, null, parentRow);
                }
            });
        });
    }

    function toggleDetail(parentId: string, btn: HTMLButtonElement): void {
        const tHost = getTableHost();
        const detailRow = tHost.querySelector<HTMLElement>(`tr.detail-row[data-detail-for="${parentId}"]`);
        if (!detailRow) {
            return;
        }
        const open = detailRow.style.display !== "none";
        if (open) {
            detailRow.style.display = "none";
            btn.innerHTML = "&#9654;";
            return;
        }
        detailRow.style.display = "";
        btn.innerHTML = "&#9660;";
        loadDetail(parentId);
    }

    function loadDetail(parentId: string): void {
        const parentRow = parentRowsById[parentId];
        if (!parentRow || !entity.detail) {
            return;
        }
        const tHost = getTableHost();
        const hostEl = tHost.querySelector<HTMLElement>(`[data-detail-host="${parentId}"]`);
        if (!hostEl) {
            return;
        }
        hostEl.innerHTML = "Loading...";
        const parentKey = parentRow[entity.detail.parentField];
        const path =
            "?" +
            entity.detail.childParentField +
            "=" +
            encodeURIComponent(String(parentKey)) +
            "&includeInactive=true";
        api(entity.detail, "GET", path)
            .then(rows => renderChild(hostEl, entity.detail!, rows, parentRow))
            .catch(err => {
                hostEl.innerHTML = `<div class="error-msg">${escapeHtml(err.message)}</div>`;
            });
    }

    function fillForm(
        desc: EntityDef,
        row: any,
        lockedField: { key: string; value: unknown } | null,
    ): void {
        desc.fields.forEach(f => {
            const el = host.querySelector<HTMLInputElement>(`#f_${f.key}`);
            if (!el) {
                return;
            }
            const v = row
                ? row[f.key]
                : f.default !== undefined
                ? f.default
                : f.type === "bool"
                ? false
                : "";
            if (f.type === "bool") {
                el.checked = !!v;
            } else {
                el.value = v === null || v === undefined ? "" : String(v);
            }
        });
        if (lockedField) {
            const lockedEl = host.querySelector<HTMLInputElement>(`#f_${lockedField.key}`);
            if (lockedEl) {
                if (lockedEl.type === "checkbox") {
                    lockedEl.checked = !!lockedField.value;
                } else {
                    lockedEl.value = String(lockedField.value);
                }
            }
        }
        getFIdInput().value = row ? String(row.id) : "";
    }

    function readForm(
        desc: EntityDef,
        lockedField: { key: string; value: unknown } | null,
    ): Record<string, unknown> {
        const payload: Record<string, unknown> = {};
        desc.fields.forEach(f => {
            const el = host.querySelector<HTMLInputElement>(`#f_${f.key}`);
            if (!el) {
                return;
            }
            if (f.type === "bool") {
                payload[f.key] = el.checked;
            } else if (f.type === "number") {
                const raw = el.value.trim();
                if (raw === "") {
                    payload[f.key] = f.nullable ? null : f.default !== undefined ? f.default : 0;
                } else {
                    payload[f.key] = parseInt(raw, 10);
                }
            } else {
                const s = el.value.trim();
                payload[f.key] = s === "" ? null : s;
            }
        });
        if (lockedField) {
            payload[lockedField.key] = lockedField.value;
        }
        return payload;
    }

    let currentEditDesc: EntityDef | null = null;
    let currentEditLocked: { key: string; value: unknown } | null = null;
    let currentEditParentId: string | null = null;

    function openEditor(desc: EntityDef, id: string | null, parentRow: any): void {
        const isChild = !!(parentRow && entity.detail && desc === entity.detail);
        let lockedField: { key: string; value: unknown } | null = null;
        if (isChild && entity.detail) {
            lockedField = {
                key: entity.detail.childParentField,
                value: parentRow[entity.detail.parentField],
            };
        }
        currentEditDesc = desc;
        currentEditLocked = lockedField;
        currentEditParentId = parentRow ? String(parentRow.id) : null;
        buildEditor(desc, lockedField);
        const singular = desc.singular || desc.label.replace(/s$/, "");
        if (id) {
            getEditorTitle().textContent = `Edit ${singular}`;
            api(desc, "GET", `/${id}`)
                .then(r => {
                    fillForm(desc, r, lockedField);
                    getEditorBg().classList.add("show");
                })
                .catch(err => showToast(err.message, true));
        } else {
            getEditorTitle().textContent = `New ${singular}`;
            fillForm(desc, null, lockedField);
            getEditorBg().classList.add("show");
        }
    }

    function closeEditor(): void {
        getEditorBg().classList.remove("show");
    }

    function confirmDelete(desc: EntityDef, id: string, parentRow: any): void {
        if (!window.confirm("Delete this row? This cannot be undone.")) {
            return;
        }
        api(desc, "DELETE", `/${id}`)
            .then(() => {
                showToast("Deleted.");
                if (parentRow) {
                    loadDetail(String(parentRow.id));
                } else {
                    load();
                }
            })
            .catch(err => showToast(err.message, true));
    }

    function load(): void {
        let path = "";
        if (!entity.noIsActive) {
            path = `?includeInactive=${getChkInactive().checked ? "true" : "false"}`;
        }
        const tHost = getTableHost();
        tHost.innerHTML = "Loading...";
        api(entity, "GET", path)
            .then(rows => renderParent(rows))
            .catch(err => {
                getTableHost().innerHTML = `<div class="error-msg">${escapeHtml(err.message)}</div>`;
            });
    }

    const onSubmit = (e: Event) => {
        e.preventDefault();
        const id = getFIdInput().value;
        const desc = currentEditDesc || entity;
        const payload = readForm(desc, currentEditLocked);
        const p = id ? api(desc, "PUT", `/${id}`, payload) : api(desc, "POST", "", payload);
        p.then(() => {
            showToast("Saved.");
            closeEditor();
            if (currentEditParentId) {
                loadDetail(currentEditParentId);
            } else {
                load();
            }
        }).catch(err => showToast(err.message, true));
    };

    const onNew = () => openEditor(entity, null, null);
    const onReload = () => load();
    const onCancel = () => closeEditor();
    const onInactive = () => load();

    // Use event delegation on `host` so we don't depend on captured DOM
    // refs (which become detached after React re-renders).
    const onHostClick = (e: Event) => {
        const target = e.target as HTMLElement | null;
        if (!target) {
            return;
        }
        // Sidebar entity link. Bypass the Spire SPA router (which would
        // otherwise treat the link as an internal route and bounce us to
        // home) by doing a full-page navigation to the same page with a
        // different `?type=`.
        const navLink = target.closest<HTMLAnchorElement>("a[data-vca-nav]");
        if (navLink) {
            const me = e as MouseEvent;
            // Honor modifier-clicks / middle-click (open-in-new-tab etc).
            if (me.defaultPrevented || me.button !== 0 || me.metaKey || me.ctrlKey || me.shiftKey || me.altKey) {
                return;
            }
            e.preventDefault();
            window.location.assign(navLink.href);
            return;
        }
        if (target.closest('[data-vca="btnNew"]')) {
            onNew();
        } else if (target.closest('[data-vca="btnReload"]')) {
            onReload();
        } else if (target.closest('[data-vca="btnCancel"]')) {
            onCancel();
        } else if (target.matches('[data-vca="editorBg"]')) {
            // Backdrop click (only when click lands on the modal-bg itself,
            // not its children).
            closeEditor();
        }
    };
    const onHostSubmit = (e: Event) => {
        const t = e.target as HTMLElement | null;
        if (t && t.matches('form[data-vca="editorForm"]')) {
            onSubmit(e);
        }
    };
    const onHostChange = (e: Event) => {
        const t = e.target as HTMLElement | null;
        if (t && t.matches('[data-vca="chkInactive"]') && !entity.noIsActive) {
            onInactive();
        }
    };

    host.addEventListener("click", onHostClick);
    host.addEventListener("submit", onHostSubmit);
    host.addEventListener("change", onHostChange);
    if (entity.noIsActive) {
        getChkInactiveLabel().style.display = "none";
    }

    document.title = `${entity.label} - Valve Configurator Admin`;

    load();

    return () => {
        sidebarObserver.disconnect();
        host.removeEventListener("click", onHostClick);
        host.removeEventListener("submit", onHostSubmit);
        host.removeEventListener("change", onHostChange);
    };
}

const ValveConfigAdminPage: React.FC<PageProps> = () => {
    const hostRef = React.useRef<HTMLDivElement | null>(null);
    const accessToken = useSelector((state: ApplicationState) => state.context.accessToken);
    const accessTokenRef = React.useRef<string | undefined>(accessToken);
    accessTokenRef.current = accessToken;

    React.useEffect(() => {
        if (!hostRef.current) {
            return undefined;
        }
        return wireAdminUi(hostRef.current, () => accessTokenRef.current);
    }, []);

    return (
        <div className="vca-root">
            <style>{STYLES}</style>
            <div ref={hostRef} dangerouslySetInnerHTML={{ __html: SKELETON }} />
        </div>
    );
};

const pageModule: PageModule = {
    component: ValveConfigAdminPage,
    definition: {
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "Content",
        fieldDefinitions: [
            { ...HideHeader, defaultValue: false, isVisible: () => false },
            { ...HideFooter, defaultValue: true, isVisible: () => false },
            { ...HideFromSearchEngines, defaultValue: true, isVisible: () => false },
            { ...HideFromSiteSearch, defaultValue: true, isVisible: () => false },
            { ...ExcludeFromNavigation, defaultValue: true, isVisible: () => false },
        ] as FieldDefinition[],
    },
};

export default pageModule;
